import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type UseOpenPaymentMethodPortalActionProps = {
    companyId: string;
};

type UseOpenPaymentMethodPortalActionData = {
    openPaymentMethodPortal: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Opening the Stripe portal at the page for changing the card the company is billed on.
 *
 * Carries no `isAllowed` of its own: the billing tab is gated as a whole by
 * `canEditBilling` on the billing settings page, and everything on it is
 * reachable only from there.
 */
export function useOpenPaymentMethodPortalAction(
    props: UseOpenPaymentMethodPortalActionProps
): UseOpenPaymentMethodPortalActionData {
    const { companyId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const mutation = useMutation({
        mutationFn: () => {
            const hostURL = window.location.origin;
            const returnUrl = `${hostURL}/company/${companyId}/settings/billing`;

            return openApiRef.current.companies
                .createStripePaymentMethodPortalSession(companyId, {
                    returnUrl
                })
                .then((response) => {
                    // Stripe hosts the portal, so the browser leaves the app
                    // here. Nothing to write to the cache on the way out.
                    window.location.replace(response.data.data.url);
                });
        },
        onError: () => {
            // Deliberately one message for every error this endpoint declares.
            // Whether the refusal was a 403, a missing company or a bad
            // request, the user is left on this page with nothing opened, so
            // no branch would change what they do next.
            UIToast.error(t('actions.open_payment_method_portal.error'));
        }
    });

    return {
        openPaymentMethodPortal: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error
            ? t('actions.open_payment_method_portal.error')
            : ''
    };
}
